/**
 * AI Registry API Client
 *
 * Type-safe client for interacting with the AI registry API endpoints
 */

import type { AIModel } from '../pages/ai/ModelsMain';

export interface AIModelConfig {
  id: string;
  name: string;
  modelId: string;
  description: string;
  parameters: {
    temperature: number;
    topK?: number;
    topP?: number;
    maxOutputTokens: number;
    stopSequences?: string[];
  };
  useCase: 'fast' | 'balanced' | 'creative' | 'thinking' | 'custom';
  metadata: {
    createdAt: any;
    updatedAt: any;
    createdBy: string;
  };
}

export interface AIRun {
  id: string;
  flowId: string;
  flowVersion: string;
  modelId: string;
  modelConfigId: string;
  input: {
    data: Record<string, any>;
    schema: Record<string, any>;
    size: number;
  };
  output: {
    data: Record<string, any>;
    schema: Record<string, any>;
    size: number;
  };
  tokens: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  cost: {
    amount: number;
    currency: string;
  };
  performance: {
    duration: number;
    ttfb?: number;
    modelLatency: number;
    overhead: number;
  };
  result: {
    status: 'success' | 'error' | 'timeout' | 'rate_limited' | 'budget_exceeded';
    error?: {
      code: string;
      message: string;
    };
  };
  startedAt: any;
  completedAt: any;
  createdAt: any;
}

export interface Analytics {
  period: 'hour' | 'day' | 'week' | 'month';
  totalRuns: number;
  successful: number;
  failed: number;
  avgLatency: number;
  totalCost: number;
  totalTokens: number;
}

/**
 * Get Firebase ID token for authentication
 */
async function getAuthToken(): Promise<string> {
  // TODO: Implement Firebase Auth token retrieval
  // For now, return empty string - needs Firebase Auth integration
  const auth = (window as any).firebase?.auth();
  if (auth) {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
  }
  throw new Error('Not authenticated');
}

/**
 * Base API URL for admin endpoints
 */
const API_BASE_URL = import.meta.env['VITE_FIREBASE_FUNCTIONS_URL'] || 'http://localhost:5001/unwritten-dev/us-east1/apiAdminAgents';

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.message || error.error || 'API request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * AI Registry API Client
 */
export const aiRegistryClient = {
  // ============================================================================
  // Models
  // ============================================================================

  /**
   * List all models
   */
  async getModels(filters?: {
    provider?: string;
    status?: 'active' | 'deprecated' | 'beta';
  }): Promise<AIModel[]> {
    const params = new URLSearchParams();
    if (filters?.provider) params.set('provider', filters.provider);
    if (filters?.status) params.set('status', filters.status);

    const query = params.toString();
    const response = await apiRequest<{ models: AIModel[] }>(
      `/models${query ? `?${query}` : ''}`
    );
    return response.models;
  },

  /**
   * Get model by ID
   */
  async getModel(id: string): Promise<AIModel> {
    return await apiRequest<AIModel>(`/models/${id}`);
  },

  /**
   * Create/register a new model
   */
  async createModel(model: Omit<AIModel, 'id'>): Promise<AIModel> {
    return await apiRequest<AIModel>('/models', {
      method: 'POST',
      body: JSON.stringify(model),
    });
  },

  /**
   * Update model
   */
  async updateModel(id: string, updates: Partial<AIModel>): Promise<AIModel> {
    return await apiRequest<AIModel>(`/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Get recommended model for use case
   */
  async getRecommendedModel(useCase: 'fast' | 'balanced' | 'thinking'): Promise<AIModel> {
    return await apiRequest<AIModel>(`/models/recommend?useCase=${useCase}`);
  },

  // ============================================================================
  // Configs
  // ============================================================================

  /**
   * List all configs
   */
  async getConfigs(filters?: {
    modelId?: string;
    useCase?: 'fast' | 'balanced' | 'creative' | 'thinking' | 'custom';
  }): Promise<AIModelConfig[]> {
    const params = new URLSearchParams();
    if (filters?.modelId) params.set('modelId', filters.modelId);
    if (filters?.useCase) params.set('useCase', filters.useCase);

    const query = params.toString();
    const response = await apiRequest<{ configs: AIModelConfig[] }>(
      `/configs${query ? `?${query}` : ''}`
    );
    return response.configs;
  },

  /**
   * Get config by ID
   */
  async getConfig(id: string): Promise<AIModelConfig> {
    return await apiRequest<AIModelConfig>(`/configs/${id}`);
  },

  /**
   * Create a new config
   */
  async createConfig(config: Omit<AIModelConfig, 'id' | 'metadata'>): Promise<AIModelConfig> {
    return await apiRequest<AIModelConfig>('/configs', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  /**
   * Update config
   */
  async updateConfig(id: string, updates: Partial<AIModelConfig>): Promise<AIModelConfig> {
    return await apiRequest<AIModelConfig>(`/configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete config
   */
  async deleteConfig(id: string): Promise<void> {
    await apiRequest<void>(`/configs/${id}`, { method: 'DELETE' });
  },

  /**
   * Clone config
   */
  async cloneConfig(
    id: string,
    modifications: Partial<Pick<AIModelConfig, 'name' | 'description' | 'parameters'>>
  ): Promise<AIModelConfig> {
    return await apiRequest<AIModelConfig>(`/configs/${id}/clone`, {
      method: 'POST',
      body: JSON.stringify({ modifications }),
    });
  },

  /**
   * Get recommended config
   */
  async getRecommendedConfig(
    modelId: string,
    useCase: 'fast' | 'balanced' | 'creative' | 'thinking'
  ): Promise<AIModelConfig> {
    return await apiRequest<AIModelConfig>(
      `/configs/recommend?modelId=${modelId}&useCase=${useCase}`
    );
  },

  // ============================================================================
  // Runs
  // ============================================================================

  /**
   * List recent runs
   */
  async getRuns(filters?: {
    flowId?: string;
    userId?: string;
    status?: string;
    limit?: number;
  }): Promise<AIRun[]> {
    const params = new URLSearchParams();
    if (filters?.flowId) params.set('flowId', filters.flowId);
    if (filters?.userId) params.set('userId', filters.userId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit) params.set('limit', filters.limit.toString());

    const query = params.toString();
    const response = await apiRequest<{ runs: AIRun[] }>(
      `/runs${query ? `?${query}` : ''}`
    );
    return response.runs;
  },

  /**
   * Get run by ID
   */
  async getRun(id: string): Promise<AIRun> {
    return await apiRequest<AIRun>(`/runs/${id}`);
  },

  /**
   * Get analytics
   */
  async getAnalytics(filters?: {
    flowId?: string;
    period?: 'hour' | 'day' | 'week' | 'month';
  }): Promise<Analytics> {
    const params = new URLSearchParams();
    if (filters?.flowId) params.set('flowId', filters.flowId);
    if (filters?.period) params.set('period', filters.period);

    const query = params.toString();
    return await apiRequest<Analytics>(
      `/runs/analytics${query ? `?${query}` : ''}`
    );
  },

  /**
   * Get recent runs (convenience method)
   */
  async getRecentRuns(limit: number = 10): Promise<AIRun[]> {
    return this.getRuns({ limit });
  },

  // ============================================================================
  // Agents
  // ============================================================================

  /**
   * List all agents
   */
  async getAgents(filters?: {
    domain?: string;
    category?: string;
    healthStatus?: 'healthy' | 'degraded' | 'down' | 'idle';
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.domain) params.set('domain', filters.domain);
    if (filters?.category) params.set('category', filters.category);
    if (filters?.healthStatus) params.set('healthStatus', filters.healthStatus);

    const query = params.toString();
    const response = await apiRequest<{ agents?: any[]; data?: any[] }>(
      `/agents${query ? `?${query}` : ''}`
    );
    return response.agents || response.data || [];
  },

  /**
   * Get agent by ID
   */
  async getAgent(id: string): Promise<any> {
    return await apiRequest<any>(`/agents/${id}`);
  },

  /**
   * Get agent statistics
   */
  async getAgentStats(): Promise<any> {
    return await apiRequest<any>('/stats');
  },

  // ============================================================================
  // Flows
  // ============================================================================

  /**
   * List all flows
   */
  async getFlows(filters?: {
    type?: 'specialist' | 'manager' | 'orchestrator' | 'utility';
    category?: string;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.category) params.set('category', filters.category);

    const query = params.toString();
    const response = await apiRequest<{ flows: any[] }>(
      `/flows${query ? `?${query}` : ''}`
    );
    return response.flows;
  },

  /**
   * Get flow by ID
   */
  async getFlow(id: string): Promise<any> {
    return await apiRequest<any>(`/flows/${id}`);
  },

  /**
   * Get flow statistics
   */
  async getFlowStats(): Promise<any> {
    return await apiRequest<any>('/flows/stats');
  },

  // ============================================================================
  // Graph
  // ============================================================================

  /**
   * Get full dependency graph
   */
  async getDependencyGraph(): Promise<any> {
    return await apiRequest<any>('/graph');
  },

  /**
   * Get agent sub-graph
   */
  async getAgentSubGraph(agentId: string, depth: number = 2): Promise<any> {
    return await apiRequest<any>(`/agents/${agentId}/graph?depth=${depth}`);
  },
};
